import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useAppUserDefaultStore } from '../../../../../stores/appUserDefaultStore';
import { ChartAlignment } from './OverOrderingChart';

/**
 * Mirrors a rendered `TableComponent`'s geometry so the report's left-hand bar chart can
 * put bar N on the same baseline as table row N.
 *
 * Rows are MEASURED rather than assumed: cell heights vary per row (supplier logos have
 * per-platform dimensions, the meta line may wrap), so any fixed row height would drift
 * down the list.
 *
 * `page` + `itemsPerPage` reproduce the slice `TableComponent` is showing — it owns its
 * page internally and reports every change through `onPageChange`, and its per-page count
 * comes from the same user-default store read here. That way the caller can compute the
 * visible rows to chart without the table needing a new prop.
 */
export function useChartAlignment(
  /** Anything whose change means the rows re-rendered (data identity, sort, view tab). */
  contentKey: unknown
): {
  /** Attach to the element wrapping the `TableComponent`. */
  wrapRef: React.RefObject<HTMLDivElement>;
  /** Pass as the table's `onPageChange`. */
  onPageChange: (page: number) => void;
  /** Slice bounds of the table's currently visible page. */
  sliceStart: number;
  sliceEnd: number;
  alignment: ChartAlignment;
} {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);
  const itemsPerPage = useAppUserDefaultStore((s) => s.tableItemsPerPage);
  const [alignment, setAlignment] = useState<ChartAlignment>({ headerHeight: 0, rowHeights: [] });

  const measure = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const thead = el.querySelector('thead');
    const rows = Array.from(el.querySelectorAll('tbody > tr')) as HTMLElement[];
    const next: ChartAlignment = {
      headerHeight: thead?.getBoundingClientRect().height ?? 0,
      rowHeights: rows.map((r) => r.getBoundingClientRect().height),
    };
    setAlignment((prev) =>
      prev.headerHeight === next.headerHeight &&
      prev.rowHeights.length === next.rowHeights.length &&
      prev.rowHeights.every((h, i) => h === next.rowHeights[i])
        ? prev
        : next
    );
  }, []);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    measure();
    // Catches font loading, container resizes and logo images settling. Row swaps that
    // happen to keep the same total height are covered by the `contentKey` dependency.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure, contentKey, page, itemsPerPage]);

  const sliceStart = (page - 1) * itemsPerPage;
  return { wrapRef, onPageChange: setPage, sliceStart, sliceEnd: sliceStart + itemsPerPage, alignment };
}
